import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  DEFAULT_CASHBOX_AMO_FIELDS,
  DEFAULT_FLEX_AMO_CONFIG,
  FLEX_STATUSES,
  flexAmoSyncConfigured,
  type FlexStatus,
} from '@mnp/shared';
import { cashboxApi, canDoCashbox, type CashboxSettings as SettingsType } from '../../../lib/cashboxApi.js';
import { useRole } from '../../../lib/role.tsx';

// Flex AMO mapping: раньше редактировалось одним JSON-полем. Теперь — отдельные
// числовые поля, чтобы не-технический пользователь мог менять id без JSON.
// Здесь только структура формы (группы, ключи, русские лейблы); дефолты-
// плейсхолдеры берутся из DEFAULT_FLEX_AMO_CONFIG (@mnp/shared flex/amo.ts,
// общий источник с API). Воронка и этапы дефолтов НЕ имеют (§4.13): пока ID
// воронки и всех этапов не заполнены, синк Flex→AMO выключен.

type FlexGroup = 'stages' | 'fields' | 'contactFields' | 'paymentEnums';
interface FlexFieldMeta {
  key: string;
  label: string;
}
interface FlexSectionMeta {
  group: FlexGroup;
  title: string;
  items: FlexFieldMeta[];
}

// Дефолт поля из shared-конфига. 0 = дефолта нет (вся группа stages, trackNumber):
// пустое поле остаётся пустым, плейсхолдер — «не настроено». Соответствие ключей
// формы ключам DEFAULT_FLEX_AMO_CONFIG закреплено contract-тестом
// (CashboxSettings.contract.test.ts) — опечатка в ключе падает тестом.
function flexDefault(group: FlexGroup, key: string): number {
  return (DEFAULT_FLEX_AMO_CONFIG[group] as Record<string, number>)[key] ?? 0;
}

export const FLEX_SECTIONS: FlexSectionMeta[] = [
  {
    group: 'stages',
    // Этапы (как и воронка) задаются только явно — дефолтов нет, чтобы заявки
    // не утекали в тестовую воронку (§4.13).
    title: 'Этапы воронки (ID статуса)',
    items: [
      { key: 'draft', label: 'Анкета (черновик)' },
      { key: 'submitted', label: 'Анкета заполнена' },
      { key: 'in_review', label: 'На рассмотрении' },
      { key: 'approved', label: 'Принят в работу' },
      { key: 'pickup', label: 'Прибыло на склад' },
      { key: 'preparing', label: 'Готовится к отправке' },
      { key: 'customs', label: 'Таможня' },
      { key: 'in_transit', label: 'В пути' },
      { key: 'assigned', label: 'Назначен курьер' },
      { key: 'delivery', label: 'Доставка' },
      { key: 'delivered', label: 'Доставлено (успешно)' },
      { key: 'rejected', label: 'Отклонено / закрыто' },
    ],
  },
  {
    group: 'fields',
    title: 'Кастом-поля сделки (ID поля)',
    items: [
      { key: 'from', label: 'Откуда' },
      { key: 'to', label: 'Куда' },
      { key: 'senderName', label: 'Имя отправителя' },
      { key: 'senderPhone', label: 'Телефон отправителя' },
      { key: 'senderAddress', label: 'Адрес отправителя' },
      { key: 'senderPassport', label: 'Паспорт отправителя' },
      { key: 'declaredPrice', label: 'Заявленная стоимость' },
      { key: 'recipientName', label: 'Имя получателя' },
      { key: 'recipientPhone', label: 'Телефон получателя' },
      { key: 'messenger', label: 'Мессенджер' },
      { key: 'apartment', label: 'Квартира' },
      { key: 'building', label: 'Дом / строение' },
      { key: 'street', label: 'Улица' },
      { key: 'city', label: 'Город' },
      { key: 'zip', label: 'Индекс' },
      { key: 'email', label: 'E-mail' },
      { key: 'geo', label: 'Геолокация' },
      { key: 'pickupFlag', label: 'Флаг забора' },
      { key: 'pickupPhone', label: 'Телефон для забора' },
      { key: 'pickupDateTime', label: 'Дата/время забора' },
      { key: 'pieces', label: 'Кол-во мест' },
      { key: 'packaging', label: 'Упаковка' },
      { key: 'budget', label: 'Бюджет / сумма' },
      { key: 'weight', label: 'Вес' },
      { key: 'places', label: 'Места' },
      { key: 'payment', label: 'Способ оплаты' },
      { key: 'paidActual', label: 'Оплачено факт' },
      { key: 'rejectedFlag', label: 'Флаг отказа' },
      { key: 'rejectComment', label: 'Комментарий отказа' },
      { key: 'trackNumber', label: 'Трек-номер' },
    ],
  },
  {
    group: 'contactFields',
    title: 'Поля контакта (ID поля)',
    items: [
      { key: 'username', label: 'Username' },
      { key: 'tgId', label: 'Telegram ID' },
    ],
  },
  {
    group: 'paymentEnums',
    title: 'Оплата: значения списка (enum ID)',
    items: [
      { key: 'cash', label: 'Наличные' },
      { key: 'transfer', label: 'Перевод' },
      { key: 'card', label: 'Карта' },
      { key: 'debt', label: 'Долг / не оплачено' },
    ],
  },
];

// Текущее эффективное значение поля: сохранённое переопределение (если > 0),
// иначе дефолт. Поля без дефолта (воронка, этапы, trackNumber) → пустая строка.
function flexInitValue(saved: unknown, def: number): string {
  const s = Number(saved);
  if (Number.isFinite(s) && s > 0) return String(Math.trunc(s));
  return def > 0 ? String(def) : '';
}

const posIntOrZero = (raw: string | undefined): number => {
  const n = parseInt(raw ?? '', 10);
  return Number.isInteger(n) && n > 0 ? n : 0;
};

// Активен ли синк Flex→AMO при текущих значениях формы. Само правило полноты
// (воронка + все 12 этапов) — flexAmoSyncConfigured из @mnp/shared, общее с API.
function flexSyncConfigured(flexValues: Record<string, string>): boolean {
  const stages: Record<string, number> = {};
  for (const it of FLEX_SECTIONS.find((s) => s.group === 'stages')?.items ?? []) {
    stages[it.key] = posIntOrZero(flexValues[`stages.${it.key}`]);
  }
  return flexAmoSyncConfigured(posIntOrZero(flexValues['pipelineId']), stages);
}

// Русские лейблы этапов берём из секции stages формы (тот же словарь, что у
// id-полей этапов прямо выше селекта границы) — UI согласован.
const FLEX_STAGE_LABELS: Record<string, string> = Object.fromEntries(
  (FLEX_SECTIONS.find((s) => s.group === 'stages')?.items ?? []).map((it) => [it.key, it.label]),
);

// Собрать amoFlexConfig из полей формы. Шлём только положительные целые; пустые
// и 0 пропускаем (сервер подставит дефолт). 0 нельзя слать: zod .positive()
// отклонит весь PATCH (400).
//
// handoffAfter (волна 4, граница «MnP ведёт сделку»): '' = граница снята → шлём
// null (бэкенд по умолчанию = null = патчим всё); иначе статус FLEX_STATUSES.
function buildFlexConfig(
  flexValues: Record<string, string>,
  handoffAfter: string,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const pid = parseInt(flexValues['pipelineId'] ?? '', 10);
  if (Number.isInteger(pid) && pid > 0) out.pipelineId = pid;
  for (const section of FLEX_SECTIONS) {
    const sub: Record<string, number> = {};
    for (const item of section.items) {
      const n = parseInt(flexValues[`${section.group}.${item.key}`] ?? '', 10);
      if (Number.isInteger(n) && n > 0) sub[item.key] = n;
    }
    if (Object.keys(sub).length > 0) out[section.group] = sub;
  }
  out.handoffAfter = (FLEX_STATUSES as readonly string[]).includes(handoffAfter)
    ? (handoffAfter as FlexStatus)
    : null;
  return out;
}

// Flex-CIS (коридор Грузия↔СНГ) AMO-маппинг. ОТДЕЛЬНЫЙ от Европы: своя воронка,
// этапы, поля и all-zero дефолты (dormant — синк не зовётся, пока не задана
// воронка). Дефолты = 0 (DEFAULT_CIS_AMO_CONFIG в flexCisOrderAmo.service.ts),
// поэтому у всех полей плейсхолдер «не настроено», а пустое поле = дефолт = 0.
// Без секции paymentEnums (у CIS пока нет AMO-оплаты).
const CIS_FLEX_SECTIONS: FlexSectionMeta[] = [
  {
    group: 'stages',
    title: 'Этапы воронки CIS (ID статуса)',
    items: [
      { key: 'draft', label: 'Анкета (черновик)' },
      { key: 'submitted', label: 'Анкета заполнена' },
      { key: 'in_review', label: 'На рассмотрении' },
      { key: 'approved', label: 'Принят в работу' },
      { key: 'pickup', label: 'Прибыло на склад' },
      { key: 'preparing', label: 'Готовится к отправке' },
      { key: 'customs', label: 'Таможня' },
      { key: 'in_transit', label: 'В пути' },
      { key: 'assigned', label: 'Назначен курьер' },
      { key: 'delivery', label: 'Доставка' },
      { key: 'delivered', label: 'Доставлено (успешно)' },
      { key: 'rejected', label: 'Отклонено / закрыто' },
    ],
  },
  {
    group: 'fields',
    title: 'Кастом-поля сделки CIS (ID поля)',
    items: [
      { key: 'from', label: 'Откуда' },
      { key: 'to', label: 'Куда' },
      { key: 'senderName', label: 'Имя отправителя' },
      { key: 'senderPhone', label: 'Телефон отправителя' },
      { key: 'senderAddress', label: 'Адрес отправителя' },
      { key: 'senderPassport', label: 'Паспорт отправителя' },
      { key: 'pickupFlag', label: 'Флаг забора' },
      { key: 'pickupCity', label: 'Город забора' },
      { key: 'pickupAddress', label: 'Адрес забора' },
      { key: 'pickupPhone', label: 'Телефон для забора' },
      { key: 'recipientsCount', label: 'Кол-во получателей' },
      { key: 'placesCount', label: 'Кол-во мест' },
      { key: 'declaredTotal', label: 'Объявленная стоимость (итого ₾)' },
      { key: 'opis', label: 'Опись (получатель→места)' },
      { key: 'cdekTrack', label: 'Трек-номер СДЭК' },
      { key: 'rejectedFlag', label: 'Флаг отказа' },
      { key: 'rejectComment', label: 'Комментарий отказа' },
    ],
  },
  {
    group: 'contactFields',
    title: 'Поля контакта CIS (ID поля)',
    items: [
      { key: 'username', label: 'Username' },
      { key: 'tgId', label: 'Telegram ID' },
    ],
  },
];

// Собрать amoFlexCisConfig (та же логика, что buildFlexConfig, но по CIS-секциям).
function buildCisFlexConfig(values: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const pid = parseInt(values['pipelineId'] ?? '', 10);
  if (Number.isInteger(pid) && pid > 0) out.pipelineId = pid;
  for (const section of CIS_FLEX_SECTIONS) {
    const sub: Record<string, number> = {};
    for (const item of section.items) {
      const n = parseInt(values[`${section.group}.${item.key}`] ?? '', 10);
      if (Number.isInteger(n) && n > 0) sub[item.key] = n;
    }
    if (Object.keys(sub).length > 0) out[section.group] = sub;
  }
  return out;
}

export function CashboxSettings() {
  const role = useRole();
  const qc = useQueryClient();
  const canManage = canDoCashbox(role, 'settings');

  const { data: settings, isLoading } = useQuery({
    queryKey: ['cashbox', 'settings'],
    queryFn: () => cashboxApi.getSettings(),
  });

  const { data: rates } = useQuery({
    queryKey: ['cashbox', 'rates'],
    queryFn: () => cashboxApi.getRates(),
  });

  const save = useMutation({
    mutationFn: (data: Partial<Omit<SettingsType, 'id'>>) => cashboxApi.updateSettings(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashbox', 'settings'] });
    },
  });

  const refreshRates = useMutation({
    mutationFn: () => cashboxApi.refreshRates(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cashbox', 'rates'] });
    },
  });

  // Local form state
  const [amoEnabled, setAmoEnabled] = useState(false);
  const [amoBaseUrl, setAmoBaseUrl] = useState('');
  const [amoFieldPaymentMethod, setAmoFieldPaymentMethod] = useState('');
  const [amoFieldTrackAmount, setAmoFieldTrackAmount] = useState('');
  const [amoAllowedPipelineIds, setAmoAllowedPipelineIds] = useState('');
  const [flexValues, setFlexValues] = useState<Record<string, string>>({});
  // Граница хэндоффа (волна 4): '' = граница снята (null на бэке), иначе статус.
  const [flexHandoffAfter, setFlexHandoffAfter] = useState('');
  const [showFlexFields, setShowFlexFields] = useState(false);
  const [cisFlexValues, setCisFlexValues] = useState<Record<string, string>>({});
  const [showCisFlexFields, setShowCisFlexFields] = useState(false);
  const [telegramChannelChatId, setTelegramChannelChatId] = useState('');
  const [dailyReportTime, setDailyReportTime] = useState('22:00');
  const [dailyReportEnabled, setDailyReportEnabled] = useState(true);
  const [notifyEachOperation, setNotifyEachOperation] = useState(true);
  const [archiveRetentionDays, setArchiveRetentionDays] = useState('7');
  const [ratesAutoRefresh, setRatesAutoRefresh] = useState(true);

  useEffect(() => {
    if (!settings) return;
    setAmoEnabled(settings.amoEnabled);
    setAmoBaseUrl(settings.amoBaseUrl ?? '');
    setAmoFieldPaymentMethod(settings.amoFieldPaymentMethod.toString());
    setAmoFieldTrackAmount(settings.amoFieldTrackAmount.toString());
    setAmoAllowedPipelineIds((settings.amoAllowedPipelineIds ?? []).join(', '));
    const cfg = (settings.amoFlexConfig ?? {}) as Record<string, Record<string, unknown> | unknown>;
    const fv: Record<string, string> = {
      // Дефолтной воронки нет (§4.13): не задана в настройках → пустое поле.
      pipelineId: flexInitValue(
        (cfg as Record<string, unknown>).pipelineId,
        DEFAULT_FLEX_AMO_CONFIG.pipelineId,
      ),
    };
    for (const section of FLEX_SECTIONS) {
      const sub = (cfg[section.group] ?? {}) as Record<string, unknown>;
      for (const item of section.items) {
        fv[`${section.group}.${item.key}`] = flexInitValue(
          sub[item.key],
          flexDefault(section.group, item.key),
        );
      }
    }
    setFlexValues(fv);
    // CIS: all-zero defs → flexInitValue gives '' for every unset field (dormant).
    const cisCfg = (settings.amoFlexCisConfig ?? {}) as Record<string, Record<string, unknown> | unknown>;
    const cisFv: Record<string, string> = {
      pipelineId: flexInitValue((cisCfg as Record<string, unknown>).pipelineId, 0),
    };
    for (const section of CIS_FLEX_SECTIONS) {
      const sub = (cisCfg[section.group] ?? {}) as Record<string, unknown>;
      for (const item of section.items) {
        cisFv[`${section.group}.${item.key}`] = flexInitValue(sub[item.key], 0);
      }
    }
    setCisFlexValues(cisFv);
    // Граница хэндоффа: валидный статус из настроек → выбран, иначе '' (нет границы).
    const savedHandoff = (cfg as Record<string, unknown>).handoffAfter;
    setFlexHandoffAfter(
      typeof savedHandoff === 'string' && (FLEX_STATUSES as readonly string[]).includes(savedHandoff)
        ? savedHandoff
        : '',
    );
    setTelegramChannelChatId(settings.telegramChannelChatId ?? '');
    setDailyReportTime(settings.dailyReportTime);
    setDailyReportEnabled(settings.dailyReportEnabled);
    setNotifyEachOperation(settings.notifyEachOperation);
    setArchiveRetentionDays(settings.archiveRetentionDays.toString());
    setRatesAutoRefresh(settings.ratesAutoRefresh);
  }, [settings]);

  function onSave() {
    // Числовые поля шлём только валидными: пустой/отрицательный/мусорный ввод →
    // дефолт, иначе сервер (zod .int().positive()) отклонит ВЕСЬ PATCH (400) и
    // форма «откатится» к старым значениям. См. баг D.
    const posIntOr = (raw: string, fallback: number) => {
      const n = parseInt(raw, 10);
      return Number.isInteger(n) && n > 0 ? n : fallback;
    };
    const nonNegIntOr = (raw: string, fallback: number) => {
      const n = parseInt(raw, 10);
      return Number.isInteger(n) && n >= 0 ? n : fallback;
    };
    save.mutate({
      amoEnabled,
      amoBaseUrl: amoBaseUrl || null,
      amoFieldPaymentMethod: posIntOr(amoFieldPaymentMethod, DEFAULT_CASHBOX_AMO_FIELDS.paymentMethod),
      amoFieldTrackAmount: posIntOr(amoFieldTrackAmount, DEFAULT_CASHBOX_AMO_FIELDS.trackAmount),
      amoAllowedPipelineIds: amoAllowedPipelineIds
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n) && n > 0),
      telegramChannelChatId: telegramChannelChatId || null,
      dailyReportTime,
      dailyReportEnabled,
      notifyEachOperation,
      archiveRetentionDays: nonNegIntOr(archiveRetentionDays, 7),
      ratesAutoRefresh,
      amoFlexConfig: buildFlexConfig(flexValues, flexHandoffAfter),
      amoFlexCisConfig: buildCisFlexConfig(cisFlexValues),
    });
  }

  if (!canManage) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>
        Управление настройками доступно только владельцу.
      </div>
    );
  }

  if (isLoading) {
    return <p style={{ padding: 32, textAlign: 'center', color: 'var(--mnp-fg-3)' }}>Загрузка…</p>;
  }

  return (
    <div style={{ maxWidth: 640 }}>
      {/* AMO section */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-head">
          <p className="panel-title">AmoCRM</p>
        </div>
        <div style={{ padding: '0 0 4px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={amoEnabled}
              onChange={(e) => setAmoEnabled(e.target.checked)}
              data-testid="amo-enabled-checkbox"
            />
            <span style={{ fontWeight: 600, fontSize: 13 }}>Синхронизация с AMO включена</span>
          </label>

          <div className="cb-field">
            <label>Базовый URL AMO</label>
            <input
              className="input-admin"
              value={amoBaseUrl}
              onChange={(e) => setAmoBaseUrl(e.target.value)}
              placeholder="https://cdekpremium.amocrm.ru"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="cb-field">
              <label>Поле AmoCRM «Способ оплаты»</label>
              <input
                className="input-admin"
                type="number"
                value={amoFieldPaymentMethod}
                onChange={(e) => setAmoFieldPaymentMethod(e.target.value)}
                placeholder="ID числового поля"
              />
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                ID числового поля сделки в AmoCRM
              </div>
            </div>
            <div className="cb-field">
              <label>Поле AmoCRM «Сумма по треку»</label>
              <input
                className="input-admin"
                type="number"
                value={amoFieldTrackAmount}
                onChange={(e) => setAmoFieldTrackAmount(e.target.value)}
                placeholder="ID числового поля"
                data-testid="amo-field-track-amount"
              />
              <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                ID числового поля сделки в AmoCRM
              </div>
            </div>
          </div>

          <div className="cb-field" style={{ marginTop: 12 }}>
            <label>Разрешённые воронки (ID через запятую)</label>
            <input
              className="input-admin"
              value={amoAllowedPipelineIds}
              onChange={(e) => setAmoAllowedPipelineIds(e.target.value)}
              placeholder="например: 10000001, 10000002"
              data-testid="amo-allowed-pipelines"
            />
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Касса синхронизирует только сделки этих воронок. Пусто — синхронизация
              отключена (защита от затирания чужих сделок в общем AmoCRM).
            </div>
          </div>

          <div className="cb-field" style={{ marginTop: 16 }}>
            <div className="f-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ margin: 0 }}>Flex: соответствие полей AmoCRM</label>
              <button
                type="button"
                className="btn-admin sm"
                onClick={() => setShowFlexFields((v) => !v)}
                data-testid="amo-flex-toggle"
              >
                {showFlexFields ? 'Скрыть' : 'Показать'}
              </button>
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              ID воронки, этапов, кастом-полей и значений оплаты для Flex-сделок.
              Для кастом-полей и значений оплаты пустое поле = значение по
              умолчанию из кода. Воронка и этапы дефолта не имеют: пока они не
              заполнены, синк Flex→AMO выключен.
            </div>
            {settings && !flexSyncConfigured(flexValues) && (
              <div
                style={{ color: 'var(--mnp-red-dark)', fontSize: 12, marginTop: 6, fontWeight: 600 }}
                data-testid="amo-flex-not-configured"
              >
                Не настроено — синк Flex→AMO выключен. Укажите ID воронки и всех
                12 этапов; до этого новые Flex-заявки получают метку «AMO не настроен».
              </div>
            )}

            {showFlexFields && (
              <div style={{ marginTop: 12 }}>
                <div className="cb-field">
                  <label>ID воронки (pipeline)</label>
                  <input
                    className="input-admin"
                    type="number"
                    value={flexValues['pipelineId'] ?? ''}
                    onChange={(e) =>
                      setFlexValues((p) => ({ ...p, pipelineId: e.target.value }))
                    }
                    placeholder="не настроено"
                    data-testid="amo-flex-pipeline-id"
                  />
                </div>

                <div className="cb-field" style={{ marginTop: 12 }}>
                  <label>Граница «MnP ведёт сделку»</label>
                  <select
                    className="input-admin"
                    value={flexHandoffAfter}
                    onChange={(e) => setFlexHandoffAfter(e.target.value)}
                    data-testid="amo-flex-handoff-after"
                  >
                    <option value="">— (нет границы)</option>
                    {FLEX_STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {FLEX_STAGE_LABELS[s] ?? s}
                      </option>
                    ))}
                  </select>
                  <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                    После этого статуса сделку ведут менеджеры в AMO вручную —
                    MnP перестаёт двигать колонку и патчить поля сделки. «Нет
                    границы» = MnP патчит сделку на всех статусах (как сейчас).
                  </div>
                </div>

                {FLEX_SECTIONS.map((section) => (
                  <div key={section.group} style={{ marginTop: 16 }}>
                    <p className="panel-title" style={{ fontSize: 12, marginBottom: 8 }}>
                      {section.title}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {section.items.map((item) => {
                        const fieldKey = `${section.group}.${item.key}`;
                        const def = flexDefault(section.group, item.key);
                        return (
                          <div className="cb-field" key={fieldKey}>
                            <label>{item.label}</label>
                            <input
                              className="input-admin"
                              type="number"
                              value={flexValues[fieldKey] ?? ''}
                              onChange={(e) =>
                                setFlexValues((p) => ({ ...p, [fieldKey]: e.target.value }))
                              }
                              placeholder={def > 0 ? String(def) : 'не настроено'}
                              data-testid={`amo-flex-${section.group}-${item.key}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Flex-CIS (Грузия↔СНГ) AMO mapping — отдельная воронка, dormant по умолчанию */}
          <div className="cb-field" style={{ marginTop: 16 }}>
            <div className="f-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <label style={{ margin: 0 }}>Flex-CIS (СНГ): соответствие полей AmoCRM</label>
              <button
                type="button"
                className="btn-admin sm"
                onClick={() => setShowCisFlexFields((v) => !v)}
                data-testid="amo-flex-cis-toggle"
              >
                {showCisFlexFields ? 'Скрыть' : 'Показать'}
              </button>
            </div>
            <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
              Отдельная воронка для коридора Грузия↔СНГ. По умолчанию всё пусто —
              синхронизация CIS «спит» (не отправляет сделки в AmoCRM), пока не
              указан ID воронки. Заполните, когда воронка CIS заведена в AmoCRM.
            </div>

            {showCisFlexFields && (
              <div style={{ marginTop: 12 }}>
                <div className="cb-field">
                  <label>ID воронки CIS (pipeline)</label>
                  <input
                    className="input-admin"
                    type="number"
                    value={cisFlexValues['pipelineId'] ?? ''}
                    onChange={(e) =>
                      setCisFlexValues((p) => ({ ...p, pipelineId: e.target.value }))
                    }
                    placeholder="не настроено"
                    data-testid="amo-flex-cis-pipeline-id"
                  />
                  <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                    Пока пусто — синхронизация CIS отключена.
                  </div>
                </div>

                {CIS_FLEX_SECTIONS.map((section) => (
                  <div key={section.group} style={{ marginTop: 16 }}>
                    <p className="panel-title" style={{ fontSize: 12, marginBottom: 8 }}>
                      {section.title}
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      {section.items.map((item) => {
                        const fieldKey = `${section.group}.${item.key}`;
                        return (
                          <div className="cb-field" key={fieldKey}>
                            <label>{item.label}</label>
                            <input
                              className="input-admin"
                              type="number"
                              value={cisFlexValues[fieldKey] ?? ''}
                              onChange={(e) =>
                                setCisFlexValues((p) => ({ ...p, [fieldKey]: e.target.value }))
                              }
                              placeholder="не настроено"
                              data-testid={`amo-flex-cis-${section.group}-${item.key}`}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Telegram section */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-head">
          <p className="panel-title">Telegram</p>
        </div>
        <div style={{ padding: '0 0 4px' }}>
          <div className="cb-field">
            <label>Chat ID канала уведомлений</label>
            <input
              className="input-admin"
              value={telegramChannelChatId}
              onChange={(e) => setTelegramChannelChatId(e.target.value)}
              placeholder="-1001234567890"
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={notifyEachOperation}
              onChange={(e) => setNotifyEachOperation(e.target.checked)}
            />
            <span style={{ fontSize: 13 }}>Уведомлять о каждой операции</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={dailyReportEnabled}
              onChange={(e) => setDailyReportEnabled(e.target.checked)}
            />
            <span style={{ fontSize: 13 }}>Ежедневный отчёт в {dailyReportTime}</span>
          </label>

          <div className="cb-field">
            <label>Время ежедневного отчёта (Тбилиси)</label>
            <input
              className="input-admin"
              type="time"
              value={dailyReportTime}
              onChange={(e) => setDailyReportTime(e.target.value)}
              style={{ width: 120 }}
            />
          </div>
        </div>
      </div>

      {/* Archive section */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-head">
          <p className="panel-title">Архив</p>
        </div>
        <div style={{ padding: '0 0 4px' }}>
          <div className="cb-field">
            <label>Срок хранения удалённых операций (дней)</label>
            <input
              className="input-admin"
              type="number"
              min="1"
              max="90"
              value={archiveRetentionDays}
              onChange={(e) => setArchiveRetentionDays(e.target.value)}
              style={{ width: 100 }}
            />
          </div>
        </div>
      </div>

      {/* Rates section */}
      <div className="panel" style={{ marginBottom: 16 }}>
        <div className="panel-head">
          <p className="panel-title">Курсы валют (NBG)</p>
        </div>
        <div style={{ padding: '0 0 8px' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={ratesAutoRefresh}
              onChange={(e) => setRatesAutoRefresh(e.target.checked)}
            />
            <span style={{ fontSize: 13 }}>Автообновление каждый день в 09:00 (Тбилиси)</span>
          </label>

          {rates && rates.length > 0 && (
            <table className="data" style={{ marginBottom: 12 }} data-testid="rates-table">
              <thead>
                <tr>
                  <th>Валюта</th>
                  <th className="num">Курс к GEL</th>
                  <th>Обновлено</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.currency}>
                    <td className="bold">{r.currency}</td>
                    <td className="num tnum">{r.rate}</td>
                    <td className="muted">{new Date(r.fetchedAt).toLocaleString('ru-RU')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          <button
            className="btn-admin"
            onClick={() => refreshRates.mutate()}
            disabled={refreshRates.isPending}
            data-testid="refresh-rates-btn"
          >
            {refreshRates.isPending ? 'Обновляю…' : 'Обновить курсы сейчас'}
          </button>
        </div>
      </div>

      {/* Save */}
      {save.isError && (
        <p style={{ color: 'var(--mnp-red-dark)', fontSize: 13, marginBottom: 12 }}>
          Ошибка сохранения настроек
        </p>
      )}
      {save.isSuccess && (
        <p style={{ color: '#166534', fontSize: 13, marginBottom: 12 }}>
          Настройки сохранены
        </p>
      )}
      <button
        className="btn-admin primary"
        onClick={onSave}
        disabled={save.isPending}
        data-testid="settings-save-btn"
      >
        {save.isPending ? 'Сохраняю…' : 'Сохранить настройки'}
      </button>
    </div>
  );
}
